import type { DayObject } from 'types/graphql'

import { logger } from 'src/lib/logger'
import { NativeObjectTypes, PropertySources } from 'src/lib/objects'
import { PropertyTypes } from 'src/lib/objects'
import { renderValue } from 'src/lib/Objects/build'
import { StandardProperties } from 'src/lib/Properties/properties'

export enum ObjectReadPolicy {
  RECENCY = 'RECENCY',
  SOURCE = 'SOURCE',
}

export enum ObjectUserIdPolicy {
  FAVOR = 'FAVOR',
  EMPTY = 'EMPTY',
  EXCLUSIVE = 'EXCLUSIVE',
  ALL = 'ALL',
}

const NativeObjectTypesReadPolicies = {
  [NativeObjectTypes.Person]: ObjectReadPolicy.SOURCE,
  [NativeObjectTypes.Organization]: ObjectReadPolicy.SOURCE,
  [NativeObjectTypes.Opportunity]: ObjectReadPolicy.SOURCE,
  [NativeObjectTypes.Pipeline]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.Stage]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.MeetingRecording]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.MeetingRecordingClip]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.Page]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.Action]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.GmailThread]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.Workspace]: ObjectReadPolicy.RECENCY,
  [NativeObjectTypes.View]: ObjectReadPolicy.RECENCY,
}

const _NativeObjectTypesUserIdPolicies = {
  [NativeObjectTypes.Person]: ObjectUserIdPolicy.FAVOR,
  [NativeObjectTypes.Organization]: ObjectUserIdPolicy.EMPTY,
  [NativeObjectTypes.Opportunity]: ObjectUserIdPolicy.EMPTY,
  [NativeObjectTypes.Pipeline]: ObjectUserIdPolicy.EMPTY,
  [NativeObjectTypes.Stage]: ObjectUserIdPolicy.EMPTY,
  [NativeObjectTypes.MeetingRecording]: ObjectUserIdPolicy.EMPTY,
  [NativeObjectTypes.Action]: ObjectUserIdPolicy.ALL,
  [NativeObjectTypes.WebPage]: ObjectUserIdPolicy.EMPTY,
}

export const NativeObjectGlobalWorkspacePolicies = {
  [NativeObjectTypes.Organization]: true,
  [NativeObjectTypes.Person]: true,
  [NativeObjectTypes.Opportunity]: false,
  [NativeObjectTypes.Pipeline]: false,
  [NativeObjectTypes.Stage]: false,
  [NativeObjectTypes.MeetingRecording]: false,
  [NativeObjectTypes.Action]: false,
  [NativeObjectTypes.WebPage]: false,
}

const sourceIdToPriority = {
  manual: 1,
  import: 2,
  ai_generated: 3,
  google_contacts: 4,
  google_calendar: 5,
  gmail: 6,
  parallel_ai: 7,
  form: 8,
  legacy: 9,
  reverse_contact: 10,
  coresignal: 11,
  exact_buyer: 12,
  slack: 13,
  serp: 14,
  fullcontact: 15,
  clearbit: 100,
  existsmarker: 50,
}

const buildCustomProperties = (rows: any[], propertyDefinitions: any[]) => {
  if (!propertyDefinitions || !rows) {
    return {}
  }

  const customRows = rows.filter((row) => row.name.startsWith('custom/'))

  const userValueMap = {}
  const aiValueMap = {}
  const webValueMap = {}
  const allKeys = new Set<string>()
  const definitionsWithOptions = new Set<string>()
  const propertyDefinitionsByIds = {}
  for (const row of [...propertyDefinitions]) {
    propertyDefinitionsByIds[row.id] = row
  }
  for (const row of [...customRows].sort((a, b) => {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  })) {
    const [propertyDefinitionId, optionId] = row.name.split('/').slice(1)

    if (!propertyDefinitionId) {
      continue
    }

    // TODO: Redo this once we have a better way to handle reasoning
    const reasoning = getStopgapReasoning(row)
    const updatedAt = new Date(row.updatedAt).getTime()

    allKeys.add(propertyDefinitionId)
    if (optionId) {
      definitionsWithOptions.add(propertyDefinitionId)
      if (!userValueMap[propertyDefinitionId]) {
        userValueMap[propertyDefinitionId] = {}
      }
      if (!aiValueMap[propertyDefinitionId]) {
        aiValueMap[propertyDefinitionId] = {}
      }
      if (!webValueMap[propertyDefinitionId]) {
        webValueMap[propertyDefinitionId] = {}
      }
      if (row.propertySourceId === PropertySources.AIGenerated) {
        if (aiValueMap[propertyDefinitionId][optionId] === undefined) {
          aiValueMap[propertyDefinitionId][optionId] = {
            id: optionId,
            value: row.value === 'true',
            reasoning,
            updatedAt,
            propertySourceId: row.propertySourceId,
            sourceUserId: row.meta_sourceUserId,
          }
        }
      } else if (row.propertySourceId === PropertySources.ParallelAi) {
        if (webValueMap[propertyDefinitionId][optionId] === undefined) {
          webValueMap[propertyDefinitionId][optionId] = {
            id: optionId,
            value: row.value === 'true',
            reasoning,
            updatedAt,
            propertySourceId: row.propertySourceId,
            sourceUserId: row.meta_sourceUserId,
          }
        }
      } else {
        if (userValueMap[propertyDefinitionId][optionId] === undefined) {
          userValueMap[propertyDefinitionId][optionId] = {
            id: optionId,
            value: row.value === 'true',
            reasoning: null,
            updatedAt,
            propertySourceId: row.propertySourceId,
            sourceUserId: row.meta_sourceUserId,
          }
        }
      }
    } else {
      const propertyDefinition = propertyDefinitionsByIds[propertyDefinitionId]
      const propertyType = propertyDefinition?.propertyType?.id
      const value = renderValue(row.value, propertyType)
      if (value != null) {
        if (row.propertySourceId === PropertySources.AIGenerated) {
          if (aiValueMap[propertyDefinitionId] === undefined) {
            aiValueMap[propertyDefinitionId] = {
              value,
              reasoning,
              updatedAt,
              sourceUserId: row.meta_sourceUserId,
              propertySourceId: row.propertySourceId,
            }
          }
        } else if (row.propertySourceId === PropertySources.ParallelAi) {
          if (webValueMap[propertyDefinitionId] === undefined) {
            webValueMap[propertyDefinitionId] = {
              value,
              reasoning,
              updatedAt,
              sourceUserId: row.meta_sourceUserId,
              propertySourceId: row.propertySourceId,
            }
          }
        } else {
          if (userValueMap[propertyDefinitionId] === undefined) {
            userValueMap[propertyDefinitionId] = {
              value,
              reasoning: null,
              updatedAt,
              sourceUserId: row.meta_sourceUserId,
              propertySourceId: row.propertySourceId,
            }
          }
        }
      }
    }
  }

  const customFieldOutput = {}

  for (const key of Array.from(allKeys)) {
    if (definitionsWithOptions.has(key)) {
      const allowMultipleValues =
        propertyDefinitionsByIds[key]?.propertyType?.id ===
        PropertyTypes.MultiPicklist

      const allOptionsIds = Array.from(
        new Set([
          ...Object.keys(userValueMap[key] || {}),
          ...Object.keys(aiValueMap[key] || {}),
          ...Object.keys(webValueMap[key] || {}),
        ])
      )
      let foundValue = false
      for (const optionId of allOptionsIds) {
        if (!customFieldOutput[key]) {
          customFieldOutput[key] = {
            value: [],
            reasoning: null,
            updatedAt: null,
            propertySourceId: null,
          }
        }
        const userValue = userValueMap[key]?.[optionId]
        const aiValue = aiValueMap[key]?.[optionId]
        const webValue = webValueMap[key]?.[optionId]

        if (allowMultipleValues || !foundValue) {
          if (typeof userValue?.value === 'boolean') {
            customFieldOutput[key].value.push(userValue)
            customFieldOutput[key].reasoning = userValue.reasoning
            customFieldOutput[key].sourceUserId = userValue.sourceUserId
            if (
              new Date(userValue.updatedAt) >
              new Date(customFieldOutput[key].updatedAt)
            ) {
              customFieldOutput[key].updatedAt = userValue.updatedAt
            }
            customFieldOutput[key].propertySourceId = userValue.propertySourceId
            foundValue = true
          } else if (typeof aiValue?.value === 'boolean') {
            customFieldOutput[key].value.push(aiValue)
            customFieldOutput[key].reasoning = aiValue.reasoning
            if (
              new Date(aiValue.updatedAt) >
              new Date(customFieldOutput[key].updatedAt)
            ) {
              customFieldOutput[key].updatedAt = aiValue.updatedAt
            }
            customFieldOutput[key].propertySourceId = aiValue.propertySourceId
            customFieldOutput[key].sourceUserId = null
            foundValue = true
          } else if (typeof webValue?.value === 'boolean') {
            customFieldOutput[key].value.push(webValue)
            customFieldOutput[key].reasoning = webValue.reasoning
            customFieldOutput[key].sourceUserId = null
            if (
              new Date(webValue.updatedAt) >
              new Date(customFieldOutput[key].updatedAt)
            ) {
              customFieldOutput[key].updatedAt = webValue.updatedAt
            }
            customFieldOutput[key].propertySourceId = webValue.propertySourceId
            foundValue = true
          }
        }
      }
    } else {
      if (propertyDefinitions?.length > 0) {
        const userValue = userValueMap[key]
        const aiValue = aiValueMap[key]
        const webValue = webValueMap[key]
        if (userValue) {
          customFieldOutput[key] = userValue
        } else if (userValue?.value !== false) {
          if (aiValue) {
            customFieldOutput[key] = aiValue
          } else if (webValue && aiValue?.value !== false) {
            customFieldOutput[key] = webValue
          }
        }
      }
    }
  }

  return customFieldOutput
}

const buildStandardProperties = (rows: any[]) => {
  const objectType = rows[0].objectTypeId
  const standardPropertiesArray = (rows || [])
    .map((row) => {
      const stopgapReasoning = getStopgapReasoning(row)

      const renderer =
        StandardProperties[objectType][row.name]?.renderer ||
        ((value) => renderValue(value, row.propertyType))

      return StandardProperties[objectType][row.name]
        ? {
            propertyId: row.name,
            label: StandardProperties[objectType][row.name]?.name,
            value: renderer(row.value),
            propertySource: row.propertySource,
            reasoning: stopgapReasoning,
            sourceUserId: row.meta_sourceUserId,
            propertySourceId: row.propertySourceId,
            updatedAt: row.updatedAt,
          }
        : null
    })
    .filter(Boolean)

  return Object.fromEntries(
    (standardPropertiesArray || [])
      .filter(Boolean)
      .map((property) => [property.propertyId, property])
  )
}

const buildPropertyRowsById = (rows: any[]) => {
  const propRowsById = {}
  for (const row of rows) {
    if (!propRowsById[row.objectId]) {
      propRowsById[row.objectId] = []
    }
    propRowsById[row.objectId].push(row)
  }
  return propRowsById
}

const getStopgapReasoning = (row: any) => {
  const stopgapReasoning = row.meta_sourceDescription
  if (stopgapReasoning) {
    if (
      typeof stopgapReasoning === 'string' &&
      stopgapReasoning.startsWith('{')
    ) {
      try {
        return JSON.parse(stopgapReasoning).reasoning
      } catch (e) {
        logger.warn(e)
      }
    }
  }
  return null
}

export const buildObject = ({
  objectType,
  objectId,
  workspaceId,
  rows,
  propertyDefinitions,
  userId: _userId = null,
}: {
  objectType: string
  objectId: string
  workspaceId: string
  rows: any[]
  propertyDefinitions: any[]
  userId?: string | null
}): DayObject => {
  const filteredRows = filterRows({
    rows,
    objectType,
  })

  const customProperties = buildCustomProperties(
    filteredRows,
    propertyDefinitions
  )
  const standardProperties = buildStandardProperties(filteredRows)

  const updatedAts = (rows || [])
    .map((row) => new Date(row.updatedAt).getTime())
    .filter((t) => !isNaN(t))
  const createdAts = (rows || [])
    .map((row) => new Date(row.createdAt).getTime())
    .filter((t) => !isNaN(t))

  // Calculate max/min only if arrays are not empty
  const maxUpdatedAtTimestamp =
    updatedAts.length > 0 ? Math.max(...updatedAts) : null
  const minCreatedAtTimestamp =
    createdAts.length > 0 ? Math.min(...createdAts) : null

  // Use a valid fallback date if needed (e.g., epoch or current time)
  const fallbackDate = new Date(0) // Using Epoch as a fallback

  const finalUpdatedAt =
    maxUpdatedAtTimestamp !== null
      ? new Date(maxUpdatedAtTimestamp)
      : fallbackDate
  const fallbackCreatedAtDate =
    minCreatedAtTimestamp !== null
      ? new Date(minCreatedAtTimestamp)
      : fallbackDate

  const existsRow = rows.find((row) => row.name === 'exists')
  const createdAt = existsRow ? new Date(existsRow.createdAt) : null

  standardProperties['exists'] = {
    propertyId: 'exists',
    value: createdAt
      ? createdAt.toISOString()
      : fallbackCreatedAtDate.toISOString(), // Store as ISO string
    propertySource: PropertySources.AIGenerated,
    reasoning: 'First seen by Day.ai and added to the workspace',
    sourceUserId: null,
    propertySourceId: PropertySources.AIGenerated,
    updatedAt: createdAt || fallbackCreatedAtDate,
    createdAt: createdAt || fallbackCreatedAtDate,
  }

  const object = {
    objectType,
    objectId,
    workspaceId,
    properties: {
      custom: customProperties,
      standard: standardProperties,
    },
    createdAt: (createdAt || fallbackCreatedAtDate).toISOString(),
    updatedAt: finalUpdatedAt.toISOString(),
  }

  return object
}

const filterRows = ({
  rows,
  objectType,
}: {
  rows: any[]
  objectType: string
}) => {
  const policy = NativeObjectTypesReadPolicies[objectType]
  let filteredRows = []
  if (policy === ObjectReadPolicy.RECENCY) {
    filteredRows = rows.sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
  } else if (policy === ObjectReadPolicy.SOURCE) {
    filteredRows = rows.sort((a, b) => {
      const aPriority = sourceIdToPriority[a.propertySourceId]
      const bPriority = sourceIdToPriority[b.propertySourceId]
      return aPriority - bPriority
    })
  }
  return filteredRows
}

export const buildObjects = (
  workspaceId,
  rows: any[],
  propertyDefinitions: any[]
) => {
  const rowsById = buildPropertyRowsById(rows)
  const objects = []
  for (const objectId of Object.keys(rowsById)) {
    objects.push(
      buildObject({
        objectId,
        rows: rowsById[objectId],
        propertyDefinitions,
        objectType: rowsById[objectId][0].objectTypeId,
        workspaceId,
      })
    )
  }
  return objects
}
